import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, FileText, Plug } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

/**
 * Prefer labeled incompleteJobs from summary; fall back to bare jobIds.
 * @param {object} importSummary
 * @param {'all'|'connector'|'file'} sourceFilter
 * @returns {object[]}
 */
function resolveReviewJobs(importSummary, sourceFilter = 'all') {
  const incomplete = Array.isArray(importSummary?.incompleteJobs)
    ? importSummary.incompleteJobs.filter(
        (j) =>
          j?.needsUserAction === true ||
          j?.pipelineStatus === 'PENDING_REVIEW' ||
          j?.pipelineStatus === 'NEEDS_USER_CLASSIFICATION',
      )
    : [];

  let jobs;
  if (incomplete.length) {
    jobs = incomplete.map((j) => ({
      id: j.id,
      label: j.label || j.filename || (j.connectorId ? `${j.connectorId} spend` : 'Import'),
      source: j.source || (j.ingestMode === 'CONNECTOR' ? 'connector' : 'file'),
      pendingReview: j.pendingReview,
      createdAt: j.createdAt,
      pipelineStatus: j.pipelineStatus,
    }));
  } else {
    jobs = (importSummary?.jobIds || []).map((id) => ({
      id,
      label: 'Import',
      source: 'file',
      pendingReview: null,
      createdAt: null,
      pipelineStatus: 'PENDING_REVIEW',
    }));
  }

  if (sourceFilter === 'connector') {
    return jobs.filter((j) => j.source === 'connector');
  }
  if (sourceFilter === 'file') {
    return jobs.filter((j) => j.source !== 'connector');
  }
  return jobs;
}

function formatCreatedAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

/**
 * @param {object} props
 * @param {object} props.importSummary
 * @param {'all'|'connector'|'file'} [props.sourceFilter='all']
 * @param {string} [props.title]
 * @param {string} [props.viewAllTo='/intelligent-import']
 * @param {string} [props.viewAllLabel]
 */
const PendingImportsPanel = ({
  importSummary,
  sourceFilter = 'all',
  title,
  viewAllTo = '/intelligent-import',
  viewAllLabel,
}) => {
  const jobs = useMemo(
    () => resolveReviewJobs(importSummary, sourceFilter),
    [importSummary, sourceFilter],
  );

  if (!jobs.length) {
    return null;
  }

  const count = jobs.length;
  const heading =
    title ||
    (sourceFilter === 'connector' ? 'Connector imports awaiting review' : 'Imports awaiting review');
  const description =
    sourceFilter === 'connector'
      ? `${count} connected-system import${count === 1 ? '' : 's'} need classification or factor review before commit.`
      : `${count} import${count === 1 ? '' : 's'} need classification or factor review before commit.`;
  const allLabel = viewAllLabel || (sourceFilter === 'connector' ? 'View all Smart Imports' : 'View all imports');

  return (
    <Card className='bg-card text-card-foreground border-border'>
      <CardHeader className='pb-2'>
        <CardTitle className='flex items-center gap-2 text-base'>
          <Clock size={16} strokeWidth={1.5} />
          {heading}
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-sm text-muted-foreground'>{description}</p>

        <ul className='divide-y divide-border rounded-md border border-border overflow-hidden'>
          {jobs.slice(0, 8).map((job) => {
            const dateLabel = formatCreatedAt(job.createdAt);
            const meta = [
              job.source === 'connector' ? 'Connected system' : 'Uploaded file',
              dateLabel,
              job.pendingReview != null ? `${job.pendingReview} to review` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            const Icon = job.source === 'connector' ? Plug : FileText;
            return (
              <li key={job.id} className='flex items-center gap-3 px-3 py-2.5 bg-background hover:bg-muted/40'>
                <Icon size={16} strokeWidth={1.5} className='shrink-0 text-muted-foreground' />
                <div className='min-w-0 flex-1'>
                  <p className='text-sm font-medium text-foreground truncate' title={job.label}>
                    {job.label}
                  </p>
                  {meta ? <p className='text-xs text-muted-foreground truncate'>{meta}</p> : null}
                </div>
                <Button variant='outline' size='sm' className='shrink-0' asChild>
                  <Link to={`/intelligent-import/${job.id}`}>
                    Review
                    <ArrowRight size={14} strokeWidth={1.5} className='ml-1' />
                  </Link>
                </Button>
              </li>
            );
          })}
        </ul>

        <div className='flex justify-end'>
          <Button variant='ghost' size='sm' asChild>
            <Link to={viewAllTo}>
              {allLabel}
              <ArrowRight size={14} strokeWidth={1.5} className='ml-1' />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingImportsPanel;
